/**
 * Shared look and formatting for the CRM Analytics tabs.
 *
 * Lifted out of AgentAnalyticsPanel (design ref: client mockup
 * leaderboard-v2.html) so Overview, Agents and Agent Usage stay visually one
 * product rather than three panels that drifted apart.
 */

export const TEAL = '#55C5D0';

/**
 * Applied through inline style objects, which no stylesheet can override, so the
 * values are CSS variables rather than literal hexes: index.css holds the light
 * values (unchanged from the mockup) and styles/theme-dark.css redefines them for
 * dark mode. The brand aquamarine is a literal - it reads well on either ground.
 */
export const CLR = {
    aqua: '#55c5d0', aquaDark: 'var(--a-aqua-deep)', gray: 'var(--a-gray)',
    grayLight: 'var(--a-gray-light)', border: 'var(--a-border)',
    black: 'var(--a-ink)', white: 'var(--c-panel)',
    amber: 'var(--a-amber)', amberBg: 'var(--a-amber-bg)',
    green: 'var(--a-green)', greenBg: 'var(--a-green-bg)',
    red: 'var(--a-red)', redBg: 'var(--a-red-bg)', text: 'var(--a-ink)',
};

export const fmtMoney = (n) => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
    return `$${Math.round(v)}`;
};

/** One-decimal money for finer figures (Rev / lead), e.g. $3.0k. */
export const fmtMoneyFine = (n) => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${Math.round(v)}`;
};

export const fmtNumber = (n) => (Number(n) || 0).toLocaleString();

export const initials = (name = '') =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

export const pillPalette = {
    good: { bg: CLR.greenBg, fg: CLR.green },
    mid: { bg: CLR.amberBg, fg: CLR.amber },
    bad: { bg: CLR.redBg, fg: CLR.red },
};

// `invert` flips the comparison for "lower is better" rates (e.g. a
// withdrawal rate, where a high % is bad rather than good).
export const pillTier = (v, [good, mid], invert = false) => {
    if (invert) return v <= good ? 'good' : v <= mid ? 'mid' : 'bad';
    return v >= good ? 'good' : v >= mid ? 'mid' : 'bad';
};

/**
 * Engagement statuses, matching the backend's four buckets.
 * Christian's brief used a traffic-light metaphor; these are the same four
 * states with the CRM's palette so they sit alongside the rest of the UI.
 */
export const ENGAGEMENT = {
    power_user: { label: 'Power User', bg: CLR.greenBg, fg: CLR.green, dot: CLR.green },
    active: { label: 'Active', bg: '#e6f4f6', fg: CLR.aquaDark, dot: CLR.aqua },
    low_usage: { label: 'Low Usage', bg: CLR.amberBg, fg: CLR.amber, dot: CLR.amber },
    inactive: { label: 'Inactive', bg: CLR.redBg, fg: CLR.red, dot: CLR.red },
};

/** Score bands for the CRM Utilisation Score /100. */
export const scoreTier = (score) => {
    if (score >= 80) return 'good';
    if (score >= 50) return 'mid';
    return 'bad';
};

/** How much of the score was actually measured, not inferred. */
export const CONFIDENCE = {
    high: { label: 'High confidence', fg: CLR.green },
    medium: { label: 'Partly measured', fg: CLR.amber },
    low: { label: 'Limited data', fg: CLR.red },
};

/**
 * Render a value the way the API says to.
 *
 * Every CRM Analytics section declares a `format` alongside each number, so the
 * panels never have to know what a given metric means. Lives here rather than
 * in CrmSectionPanel because the agent profile needs it too, and importing it
 * from the panel would make the two modules import each other.
 */
export const formatValue = (value, format) => {
    if (value === null || value === undefined || value === '') return '\u2014';
    switch (format) {
        case 'money':
            return fmtMoney(value);
        case 'pct':
            return `${Number(value).toFixed(1)}%`;
        case 'decimal':
            return Number(value).toFixed(1);
        case 'hours': {
            const hours = Number(value) || 0;
            if (!hours) return '\u2014';
            if (hours < 1) return `${Math.round(hours * 60)}m`;
            if (hours < 48) return `${hours.toFixed(1)}h`;
            return `${(hours / 24).toFixed(1)}d`;
        }
        case 'days':
            return `${fmtNumber(Math.round(Number(value)))}d`;
        case 'text':
            return value;
        default:
            return fmtNumber(value);
    }
};

export const relativeDay = (iso) => {
    if (!iso) return 'Never';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '—';
    const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
};
